import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  createNodeDiscussionMessage,
  getNodeDiscussion,
  listNodeDiscussions,
  markNodeDiscussionRead,
} from '../../utils/mindmap/apiClient';
import {
  parseNodeDiscussionChangedEvent,
} from '../../utils/mindmap/nodeDiscussionEvents';

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function mergeMessages(older, newer) {
  const byId = new Map();
  [...older, ...newer].forEach((message) => byId.set(message.id, message));
  return [...byId.values()].sort((left, right) => left.sequence - right.sequence);
}

export function useNodeDiscussions({
  mindmapId,
  visibleNodeIds,
  enabled,
  provider,
  onError,
}) {
  const eligibleNodeKey = [...new Set(visibleNodeIds || [])]
    .filter((nodeId) => UUID_PATTERN.test(nodeId))
    .slice(0, 100)
    .join(',');
  const eligibleNodeIds = useMemo(
    () => (eligibleNodeKey ? eligibleNodeKey.split(',') : []),
    [eligibleNodeKey]
  );
  const [summaries, setSummaries] = useState({});
  const [activeNodeId, setActiveNodeId] = useState(null);
  const [thread, setThread] = useState(null);
  const [isLoadingThread, setIsLoadingThread] = useState(false);
  const [isLoadingOlder, setIsLoadingOlder] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState(null);
  const requestVersionRef = useRef(0);

  const reportError = useCallback((nextError) => {
    setError(nextError);
    onError?.(nextError);
  }, [onError]);

  const refreshSummaries = useCallback(async () => {
    if (!enabled || !mindmapId || eligibleNodeIds.length === 0) {
      setSummaries({});
      return;
    }
    try {
      const payload = await listNodeDiscussions(mindmapId, eligibleNodeIds);
      setSummaries(Object.fromEntries(
        (payload.discussions || []).map((summary) => [summary.nodeId, summary])
      ));
    } catch (nextError) {
      reportError(nextError);
    }
  }, [eligibleNodeIds, enabled, mindmapId, reportError]);

  const acknowledgeRead = useCallback(async (nodeId, lastReadSequence) => {
    if (!enabled || !mindmapId || !nodeId || !lastReadSequence) return;
    try {
      const result = await markNodeDiscussionRead(mindmapId, nodeId, lastReadSequence);
      setSummaries((previous) => {
        const summary = previous[nodeId];
        if (!summary) return previous;
        return {
          ...previous,
          [nodeId]: {
            ...summary,
            lastReadSequence: result.lastReadSequence,
            unreadCount: Math.max(
              summary.lastMessageSequence - result.lastReadSequence,
              0
            ),
          },
        };
      });
      setThread((previous) => (
        previous?.discussion?.nodeId === nodeId
          ? {
              ...previous,
              discussion: {
                ...previous.discussion,
                lastReadSequence: result.lastReadSequence,
                unreadCount: Math.max(
                  previous.discussion.lastMessageSequence - result.lastReadSequence,
                  0
                ),
              },
            }
          : previous
      ));
    } catch (nextError) {
      reportError(nextError);
    }
  }, [enabled, mindmapId, reportError]);

  const fetchThread = useCallback(async (nodeId, { quiet = false } = {}) => {
    if (!enabled || !mindmapId || !nodeId) return null;
    const requestVersion = requestVersionRef.current + 1;
    requestVersionRef.current = requestVersion;
    if (!quiet) setIsLoadingThread(true);
    setError(null);
    try {
      const payload = await getNodeDiscussion(mindmapId, nodeId);
      if (requestVersionRef.current !== requestVersion) return null;
      setThread(payload);
      const latestSequence = payload.discussion?.lastMessageSequence || 0;
      if (latestSequence > (payload.discussion?.lastReadSequence || 0)) {
        void acknowledgeRead(nodeId, latestSequence);
      }
      return payload;
    } catch (nextError) {
      if (requestVersionRef.current === requestVersion) reportError(nextError);
      return null;
    } finally {
      if (!quiet && requestVersionRef.current === requestVersion) {
        setIsLoadingThread(false);
      }
    }
  }, [acknowledgeRead, enabled, mindmapId, reportError]);

  const openDiscussion = useCallback((nodeId) => {
    if (!enabled || !nodeId) return;
    setActiveNodeId(nodeId);
    setThread(null);
    void fetchThread(nodeId);
  }, [enabled, fetchThread]);

  const closeDiscussion = useCallback(() => {
    requestVersionRef.current += 1;
    setActiveNodeId(null);
    setThread(null);
    setError(null);
    setIsLoadingThread(false);
  }, []);

  const loadOlder = useCallback(async () => {
    const beforeSequence = thread?.page?.nextBeforeSequence;
    if (!activeNodeId || !beforeSequence || isLoadingOlder) return;
    setIsLoadingOlder(true);
    setError(null);
    try {
      const payload = await getNodeDiscussion(mindmapId, activeNodeId, {
        beforeSequence,
      });
      setThread((previous) => ({
        discussion: previous?.discussion || payload.discussion,
        messages: mergeMessages(payload.messages || [], previous?.messages || []),
        page: payload.page,
      }));
    } catch (nextError) {
      reportError(nextError);
    } finally {
      setIsLoadingOlder(false);
    }
  }, [
    activeNodeId,
    isLoadingOlder,
    mindmapId,
    reportError,
    thread?.page?.nextBeforeSequence,
  ]);

  const sendMessage = useCallback(async (body) => {
    const trimmedBody = typeof body === 'string' ? body.trim() : '';
    if (!activeNodeId || !trimmedBody || isSending) return false;
    setIsSending(true);
    setError(null);
    try {
      const result = await createNodeDiscussionMessage(
        mindmapId,
        activeNodeId,
        trimmedBody
      );
      setThread((previous) => ({
        discussion: {
          ...(previous?.discussion || {
            id: result.discussionId,
            nodeId: activeNodeId,
            createdAt: result.message.createdAt,
          }),
          id: result.discussionId,
          nodeId: activeNodeId,
          lastMessageSequence: result.message.sequence,
          lastReadSequence: result.message.sequence,
          unreadCount: 0,
          updatedAt: result.message.createdAt,
        },
        messages: mergeMessages(previous?.messages || [], [result.message]),
        page: previous?.page || {
          limit: 50,
          hasMore: false,
          nextBeforeSequence: null,
        },
      }));
      setSummaries((previous) => ({
        ...previous,
        [activeNodeId]: {
          discussionId: result.discussionId,
          nodeId: activeNodeId,
          latestMessageId: result.message.id,
          lastMessageSequence: result.message.sequence,
          lastReadSequence: result.message.sequence,
          unreadCount: 0,
          updatedAt: result.message.createdAt,
        },
      }));
      return true;
    } catch (nextError) {
      reportError(nextError);
      return false;
    } finally {
      setIsSending(false);
    }
  }, [activeNodeId, isSending, mindmapId, reportError]);

  useEffect(() => {
    void refreshSummaries();
  }, [refreshSummaries]);

  useEffect(() => {
    if (!enabled) closeDiscussion();
  }, [closeDiscussion, enabled]);

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return undefined;
    const refreshOnFocus = () => {
      void refreshSummaries();
      if (activeNodeId) void fetchThread(activeNodeId, { quiet: true });
    };
    window.addEventListener('focus', refreshOnFocus);
    return () => window.removeEventListener('focus', refreshOnFocus);
  }, [activeNodeId, enabled, fetchThread, refreshSummaries]);

  useEffect(() => {
    if (
      !enabled
      || !provider
      || typeof provider.on !== 'function'
      || typeof provider.off !== 'function'
    ) {
      return undefined;
    }
    const pendingNodeIds = new Set();
    let refreshTimer = null;
    const flushRefresh = () => {
      refreshTimer = null;
      const affectedNodeIds = new Set(pendingNodeIds);
      pendingNodeIds.clear();
      if (eligibleNodeIds.some((nodeId) => affectedNodeIds.has(nodeId))) {
        void refreshSummaries();
      }
      if (activeNodeId && affectedNodeIds.has(activeNodeId)) {
        void fetchThread(activeNodeId, { quiet: true });
      }
    };
    const scheduleRefresh = (nodeId) => {
      pendingNodeIds.add(nodeId);
      if (refreshTimer) clearTimeout(refreshTimer);
      refreshTimer = setTimeout(flushRefresh, 150);
    };
    const onStateless = ({ payload } = {}) => {
      const event = parseNodeDiscussionChangedEvent(payload, mindmapId);
      if (event) scheduleRefresh(event.nodeId);
    };
    const onStatus = ({ status } = {}) => {
      if (status !== 'connected') return;
      void refreshSummaries();
      if (activeNodeId) void fetchThread(activeNodeId, { quiet: true });
    };

    provider.on('stateless', onStateless);
    provider.on('status', onStatus);
    return () => {
      if (refreshTimer) clearTimeout(refreshTimer);
      provider.off('stateless', onStateless);
      provider.off('status', onStatus);
    };
  }, [
    activeNodeId,
    eligibleNodeIds,
    enabled,
    fetchThread,
    mindmapId,
    provider,
    refreshSummaries,
  ]);

  return {
    summaries,
    activeNodeId,
    thread,
    isLoadingThread,
    isLoadingOlder,
    isSending,
    error,
    openDiscussion,
    closeDiscussion,
    loadOlder,
    sendMessage,
    refreshSummaries,
    refreshActiveDiscussion: () => (
      activeNodeId ? fetchThread(activeNodeId, { quiet: true }) : Promise.resolve(null)
    ),
  };
}
